import React, { useState, useEffect, createContext, useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Button, TextField, MenuItem,
  CircularProgress, Alert, Chip, Avatar, Tab, Tabs, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  LinearProgress, Stack, Paper, IconButton, AppBar, Toolbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Stepper,
  Step, StepLabel, Grid
} from '@mui/material';
import {
  School, Logout, Person, CalendarMonth, BarChart,
  CheckCircle, Cancel, Schedule, EventAvailable,
  Add, ArrowBack, Verified, ChildCare, Key,
  AccountBalance, Payment as PayIcon, FileDownload, Gavel, MenuBook, AssignmentReturn, Assignment as HWIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ── Auth context ──────────────────────────────────────────────────────────────
const ParentAuthContext = createContext(null);
const useParentAuth = () => useContext(ParentAuthContext);

function apiCall(path, opts = {}) {
  const token = localStorage.getItem('parent_token');
  const slug  = localStorage.getItem('parent_slug');
  return fetch(`${API}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(slug  ? { 'X-School-Slug': slug } : {}),
    },
    ...opts
  }).then(async r => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.message || 'Request failed');
    return data;
  });
}

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS = {
  present: { label: 'Present', color: 'success', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
  absent:  { label: 'Absent',  color: 'error',   icon: <Cancel sx={{ fontSize: 14 }} /> },
  late:    { label: 'Late',    color: 'warning',  icon: <Schedule sx={{ fontSize: 14 }} /> },
  excused: { label: 'Excused', color: 'info',     icon: <EventAvailable sx={{ fontSize: 14 }} /> },
};

// ── Registration / Vetting ────────────────────────────────────────────────────
function RegisterPage({ onSuccess }) {
  const { enqueueSnackbar } = useSnackbar();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [form, setForm] = useState({
    admission_number: '', class_id: '', gender: '', parent_phone: '',
    date_of_birth: '', full_name: '', password: '', confirm_password: '',
    academic_year_id: ''
  });
  const [error, setError] = useState('');

  useEffect(() => {
    apiCall('/parent/classes').then(r => setClasses(r.data)).catch(() => {});
    apiCall('/parent/academic-years').then(r => {
      setAcademicYears(r.data);
      const cur = r.data.find(y => y.is_current);
      if (cur) setForm(f => ({ ...f, academic_year_id: cur.id }));
    }).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setError('');
    if (form.password !== form.confirm_password) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await apiCall('/parent/register', {
        method: 'POST',
        body: JSON.stringify(form)
      });
      enqueueSnackbar(res.message, { variant: 'success' });
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  const steps = ['Verify Child', 'Create Account'];

  return (
    <Box sx={{ maxWidth: 520, mx: 'auto', pt: 4, px: 2 }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Avatar sx={{ bgcolor: '#0f2b4a', width: 56, height: 56, mx: 'auto', mb: 1 }}>
          <ChildCare sx={{ fontSize: 32 }} />
        </Avatar>
        <Typography variant="h5" fontWeight={700}>Parent Portal Registration</Typography>
        <Typography variant="body2" color="text.secondary">
          Verify your child's details to create your account
        </Typography>
      </Box>

      <Stepper activeStep={step} sx={{ mb: 3 }}>
        {steps.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
      </Stepper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          {step === 0 ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ fontSize: 12 }}>
                  Enter your child's details exactly as registered with the school.
                </Alert>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Admission Number *" value={form.admission_number}
                  onChange={set('admission_number')} placeholder="e.g. 2024/0001" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth size="small" label="Class Enrolled *" value={form.class_id}
                  onChange={set('class_id')}>
                  <MenuItem value="">Select class…</MenuItem>
                  {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}{c.stream ? ` (${c.stream})` : ''}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth size="small" label="Child's Gender *" value={form.gender}
                  onChange={set('gender')}>
                  <MenuItem value="">Select…</MenuItem>
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" type="date" label="Child's Date of Birth *"
                  value={form.date_of_birth} onChange={set('date_of_birth')} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Your Phone Number (registered with school) *"
                  value={form.parent_phone} onChange={set('parent_phone')}
                  helperText="Must match the parent phone on the child's record" />
              </Grid>
              <Grid item xs={12}>
                <TextField select fullWidth size="small" label="Academic Year *" value={form.academic_year_id}
                  onChange={set('academic_year_id')}>
                  {academicYears.map(y => (
                    <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' (Current)' : ''}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <Button fullWidth variant="contained" size="large"
                  onClick={() => {
                    if (!form.admission_number || !form.gender || !form.parent_phone || !form.date_of_birth)
                      return setError('Please fill all child verification fields');
                    setError(''); setStep(1);
                  }}
                  sx={{ bgcolor: '#0f2b4a' }}>
                  Verify Child Details →
                </Button>
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="success" icon={<Verified />}>
                  Child details verified. Now create your parent account.
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Your Full Name *" value={form.full_name}
                  onChange={set('full_name')} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" type="password" label="Create Password *"
                  value={form.password} onChange={set('password')} helperText="Minimum 6 characters" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" type="password" label="Confirm Password *"
                  value={form.confirm_password} onChange={set('confirm_password')} />
              </Grid>
              <Grid item xs={6}>
                <Button fullWidth variant="outlined" startIcon={<ArrowBack />} onClick={() => setStep(0)}>
                  Back
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button fullWidth variant="contained" size="large" onClick={submit}
                  disabled={loading} sx={{ bgcolor: '#0f2b4a' }}>
                  {loading ? <CircularProgress size={20} color="inherit" /> : 'Create Account'}
                </Button>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

// ── Login ─────────────────────────────────────────────────────────────────────
function LoginPage({ onLogin, onRegister, schoolSlug }) {
  const [phone, setPhone]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      // If we have a school slug, use the platform school parent-login endpoint
      const endpoint = schoolSlug
        ? '/platform/school/parent-login'
        : '/parent/login';
      const body = schoolSlug
        ? { email: phone, password, school_slug: schoolSlug }
        : { phone, password };
      const res = await apiCall(endpoint, { method: 'POST', body: JSON.stringify(body) });
      localStorage.setItem('parent_token', res.token);
      // Store slug so subsequent API calls route to the right school
      if (schoolSlug) localStorage.setItem('parent_slug', schoolSlug);
      onLogin(res.parent || res.user, res.children || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', pt: 6, px: 2 }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Avatar sx={{ bgcolor: '#0f2b4a', width: 64, height: 64, mx: 'auto', mb: 1.5 }}>
          <School sx={{ fontSize: 36 }} />
        </Avatar>
        <Typography variant="h5" fontWeight={700}>Parent Portal</Typography>
        {schoolSlug && (
          <Chip label={schoolSlug.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            size="small" color="primary" sx={{ mt: 0.5, fontSize: 11 }} />
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Sign in to track your child's progress</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <form onSubmit={submit}>
            <Stack spacing={2}>
              <TextField fullWidth size="small"
                label={schoolSlug ? 'Email Address' : 'Phone Number'}
                value={phone}
                onChange={e => setPhone(e.target.value)}
                autoComplete={schoolSlug ? 'email' : 'tel'} />
              <TextField fullWidth size="small" type="password" label="Password" value={password}
                onChange={e => setPassword(e.target.value)} autoComplete="current-password" />
              <Button fullWidth variant="contained" type="submit" size="large" disabled={loading}
                sx={{ bgcolor: '#0f2b4a', '&:hover': { bgcolor: '#1a3a5c' } }}>
                {loading ? <CircularProgress size={22} color="inherit" /> : 'Sign In'}
              </Button>
            </Stack>
          </form>
          <Divider sx={{ my: 2 }} />
          <Button fullWidth variant="outlined" startIcon={<Add />} onClick={onRegister}>
            Register / Add a Child
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}

// ── Child Attendance View ─────────────────────────────────────────────────────
function ChildAttendance({ student }) {
  const [records, setRecords]   = useState([]);
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [academicYears, setAY]  = useState([]);
  const [selectedYear, setYear] = useState('');

  useEffect(() => {
    apiCall('/parent/academic-years').then(r => {
      setAY(r.data);
      const cur = r.data.find(y => y.is_current);
      if (cur) setYear(cur.id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedYear) return;
    setLoading(true);
    apiCall(`/parent/child/attendance?student_id=${student.id}&academic_year_id=${selectedYear}`)
      .then(r => { setRecords(r.data); setStats(r.stats); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [student.id, selectedYear]);

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1 }}>Attendance Record</Typography>
        <TextField select size="small" label="Term" value={selectedYear} onChange={e => setYear(e.target.value)} sx={{ minWidth: 180 }}>
          {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
        </TextField>
      </Box>

      {stats && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: 'Days Recorded', val: stats.total, color: '#0f2b4a' },
            { label: 'Days Present', val: stats.present, color: '#2e7d32' },
            { label: 'Days Absent', val: stats.absent, color: '#c62828' },
            { label: 'Attendance %', val: stats.attendance_pct != null ? `${stats.attendance_pct}%` : '—',
              color: stats.attendance_pct >= 80 ? '#2e7d32' : stats.attendance_pct >= 60 ? '#e65100' : '#c62828' }
          ].map(s => (
            <Grid item xs={6} sm={3} key={s.label}>
              <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="h5" fontWeight={700} sx={{ color: s.color }}>{s.val ?? '—'}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </Paper>
            </Grid>
          ))}
          <Grid item xs={12}>
            <LinearProgress variant="determinate" value={Math.min(stats.attendance_pct || 0, 100)}
              color={stats.attendance_pct >= 80 ? 'success' : stats.attendance_pct >= 60 ? 'warning' : 'error'}
              sx={{ height: 8, borderRadius: 4 }} />
            {stats.attendance_pct < 75 && (
              <Alert severity="warning" sx={{ mt: 1, fontSize: 12 }}>
                Attendance is below the 75% minimum threshold required for promotion.
              </Alert>
            )}
          </Grid>
        </Grid>
      )}

      {loading ? <CircularProgress /> : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: '#0f2b4a' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, bgcolor: '#0f2b4a', color: 'white' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, bgcolor: '#0f2b4a', color: 'white' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, bgcolor: '#0f2b4a', color: 'white' }}>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.length === 0 && (
                  <TableRow><TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>No attendance records yet</TableCell></TableRow>
                )}
                {records.map((r, i) => {
                  const cfg = STATUS[r.status] || STATUS.present;
                  return (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontSize: 12 }}>
                        {new Date(r.attendance_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                      </TableCell>
                      <TableCell>
                        <Chip icon={cfg.icon} label={cfg.label} size="small" color={cfg.color} sx={{ fontSize: 11, fontWeight: 600 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{r.notes || '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );
}

// ── Child Scores View ─────────────────────────────────────────────────────────
function ChildScores({ student }) {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [academicYears, setAY]  = useState([]);
  const [selectedYear, setYear] = useState('');

  useEffect(() => {
    apiCall('/parent/academic-years').then(r => {
      setAY(r.data);
      const cur = r.data.find(y => y.is_current);
      if (cur) setYear(cur.id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedYear) return;
    setLoading(true);
    apiCall(`/parent/child/scores?student_id=${student.id}&academic_year_id=${selectedYear}`)
      .then(r => setSubjects(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [student.id, selectedYear]);

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1 }}>Assessment Scores</Typography>
        <TextField select size="small" label="Term" value={selectedYear} onChange={e => setYear(e.target.value)} sx={{ minWidth: 180 }}>
          {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
        </TextField>
      </Box>

      <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
        Showing individual assessment scores. Full term report cards are issued by the school.
      </Alert>

      {loading ? <CircularProgress /> : subjects.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <BarChart sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No assessment scores recorded yet for this term</Typography>
        </Paper>
      ) : (
        subjects.map(sub => {
          const avg = sub.assessments.length > 0
            ? (sub.assessments.reduce((s, a) => s + (a.percent || 0), 0) / sub.assessments.length).toFixed(1)
            : null;
          return (
            <Card key={sub.subject} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
              <CardContent sx={{ pb: '12px !important' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography fontWeight={700} fontSize={14}>{sub.subject}</Typography>
                    {sub.is_core && <Chip label="CORE" size="small" sx={{ fontSize: 9, height: 18, bgcolor: '#0f2b4a', color: 'white' }} />}
                  </Box>
                  {avg && (
                    <Chip label={`Avg: ${avg}%`} size="small"
                      color={avg >= 60 ? 'success' : avg >= 40 ? 'warning' : 'error'}
                      sx={{ fontWeight: 700 }} />
                  )}
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: 11, fontWeight: 700, p: '4px 8px' }}>Assessment</TableCell>
                      <TableCell sx={{ fontSize: 11, fontWeight: 700, p: '4px 8px' }} align="right">Score</TableCell>
                      <TableCell sx={{ fontSize: 11, fontWeight: 700, p: '4px 8px' }} align="right">Max</TableCell>
                      <TableCell sx={{ fontSize: 11, fontWeight: 700, p: '4px 8px' }} align="right">%</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sub.assessments.map((a, i) => (
                      <TableRow key={i} hover>
                        <TableCell sx={{ fontSize: 12, p: '4px 8px' }}>{a.assessment}</TableCell>
                        <TableCell sx={{ fontSize: 12, p: '4px 8px', fontWeight: 600 }} align="right">{a.score ?? '—'}</TableCell>
                        <TableCell sx={{ fontSize: 12, p: '4px 8px', color: 'text.secondary' }} align="right">{a.max_score}</TableCell>
                        <TableCell sx={{ p: '4px 8px' }} align="right">
                          <Chip label={a.percent != null ? `${a.percent}%` : '—'} size="small" variant="outlined"
                            color={a.percent >= 60 ? 'success' : a.percent >= 40 ? 'warning' : 'error'}
                            sx={{ fontSize: 10, height: 20 }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          );
        })
      )}
    </Box>
  );
}


// ── Child Fees View ───────────────────────────────────────────────────────────
function ChildFees({ student }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [academicYears, setAY]  = useState([]);
  const [selectedYear, setYear] = useState('');

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    apiCall('/parent/academic-years').then(r => {
      setAY(r.data);
      const cur = r.data.find(y => y.is_current);
      if (cur) setYear(cur.id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedYear) return;
    setLoading(true);
    apiCall(`/parent/child/fees?student_id=${student.id}&academic_year_id=${selectedYear}`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [student.id, selectedYear]);

  const downloadStatement = () => {
    const token = localStorage.getItem('parent_token');
    window.open(`${API_BASE}/parent/child/statement?student_id=${student.id}&academic_year_id=${selectedYear}&token=${token}`, '_blank');
  };

  const STATUS_COLOR = { paid: 'success', partial: 'warning', unpaid: 'error', overdue: 'error', waived: 'default' };
  const fmt = (n) => `E${parseFloat(n || 0).toLocaleString('en', { minimumFractionDigits: 2 })}`;
  const today = new Date();
  const getDueDays = (due_date) => {
    if (!due_date) return null;
    return Math.ceil((new Date(due_date) - today) / (1000 * 60 * 60 * 24));
  };

  const unpaidInvoices = data?.invoices?.filter(i => ['unpaid','partial','overdue'].includes(i.status)) || [];
  const overdueInvoices = unpaidInvoices.filter(i => i.status === 'overdue' || (i.due_date && getDueDays(i.due_date) < 0));
  const dueSoonInvoices = unpaidInvoices.filter(i => { const d = getDueDays(i.due_date); return d !== null && d >= 0 && d <= 7; });

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1 }}>Fee Account</Typography>
        <TextField select size="small" label="Term" value={selectedYear} onChange={e => setYear(e.target.value)} sx={{ minWidth: 180 }}>
          {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
        </TextField>
        {selectedYear && data?.invoices?.length > 0 && (
          <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={downloadStatement}>
            Download Statement
          </Button>
        )}
      </Box>

      {overdueInvoices.length > 0 && (
        <Alert severity="error" sx={{ mb: 2, fontSize: 12 }}>
          <strong>Overdue payment:</strong> {overdueInvoices.length} invoice(s) are overdue totalling {fmt(overdueInvoices.reduce((s, i) => s + parseFloat(i.amount) - parseFloat(i.amount_paid), 0))}. Please contact the school finance office immediately.
        </Alert>
      )}
      {dueSoonInvoices.length > 0 && overdueInvoices.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2, fontSize: 12 }}>
          <strong>Payment due soon:</strong> {dueSoonInvoices.length} invoice(s) due within 7 days. Please arrange payment to avoid penalties.
        </Alert>
      )}

      {data?.summary && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: 'Total Fees', val: fmt(data.summary.total_billed), color: '#0f2b4a' },
            { label: 'Amount Paid', val: fmt(data.summary.total_paid), color: '#2e7d32' },
            { label: 'Outstanding', val: fmt(data.summary.total_outstanding), color: data.summary.total_outstanding > 0 ? '#c62828' : '#2e7d32' },
          ].map(s => (
            <Grid item xs={4} key={s.label}>
              <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={700} sx={{ color: s.color }}>{s.val}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {loading ? <CircularProgress /> : !data?.invoices?.length ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <AccountBalance sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No fee invoices for this term</Typography>
        </Paper>
      ) : (
        <>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Invoices</Typography>
          {data.invoices.map(inv => {
            const dueDays = getDueDays(inv.due_date);
            const isOverdue = inv.status === 'overdue' || (dueDays !== null && dueDays < 0 && inv.status !== 'paid' && inv.status !== 'waived');
            const isDueSoon = !isOverdue && dueDays !== null && dueDays <= 7 && inv.status !== 'paid';
            return (
              <Card key={inv.id} variant="outlined" sx={{ mb: 1.5, borderRadius: 2, borderLeft: `4px solid ${inv.status === 'paid' ? '#2e7d32' : isOverdue ? '#b71c1c' : inv.status === 'partial' ? '#e65100' : '#c62828'}` }}>
                <CardContent sx={{ pb: '8px !important', pt: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography fontSize={13} fontWeight={700}>{inv.description}</Typography>
                      <Typography fontSize={11} color="text.secondary">{inv.invoice_number}</Typography>
                      {inv.due_date && (
                        <Typography fontSize={11} sx={{ color: isOverdue ? '#c62828' : isDueSoon ? '#e65100' : 'text.secondary', fontWeight: isOverdue || isDueSoon ? 700 : 400 }}>
                          {isOverdue ? '⚠ OVERDUE — ' : isDueSoon ? `⏰ Due in ${dueDays} day(s) — ` : 'Due: '}
                          {new Date(inv.due_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography fontSize={15} fontWeight={800} color="#0f2b4a">{fmt(inv.amount)}</Typography>
                      <Chip label={inv.status} size="small" color={STATUS_COLOR[inv.status] || 'default'} sx={{ fontSize: 10, fontWeight: 700 }} />
                    </Box>
                  </Box>
                  {(inv.status === 'partial' || (parseFloat(inv.amount_paid) > 0 && inv.status !== 'paid')) && (
                    <Box sx={{ mt: 1 }}>
                      <LinearProgress variant="determinate" value={Math.min((inv.amount_paid / inv.amount) * 100, 100)} color="warning" sx={{ height: 5, borderRadius: 3 }} />
                      <Typography fontSize={10} color="text.secondary" sx={{ mt: 0.3 }}>Paid: {fmt(inv.amount_paid)} | Remaining: {fmt(parseFloat(inv.amount) - parseFloat(inv.amount_paid))}</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {data.payments?.length > 0 && (
            <>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2, mb: 1 }}>Payment History</Typography>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>Receipt</TableCell>
                        <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>Date</TableCell>
                        <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>Amount</TableCell>
                        <TableCell sx={{ fontSize: 11, fontWeight: 700 }}>Method</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.payments.map((p, i) => (
                        <TableRow key={i} hover>
                          <TableCell sx={{ fontSize: 11, fontFamily: 'monospace' }}>{p.receipt_number}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{new Date(p.payment_date).toLocaleDateString('en-GB')}</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'success.main' }}>E{parseFloat(p.amount).toFixed(2)}</TableCell>
                          <TableCell><Chip label={p.payment_method?.replace('_',' ')} size="small" sx={{ fontSize: 10, textTransform: 'capitalize' }} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </>
          )}
        </>
      )}
    </Box>
  );
}


// ── Child Disciplinary View ───────────────────────────────────────────────────
function ChildDisciplinary({ student }) {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [academicYears, setAY]    = useState([]);
  const [selectedYear, setYear]   = useState('');

  const SEVERITY_COLOR = { minor:'default', moderate:'warning', serious:'error', critical:'error' };
  const SEVERITY_LABEL = { minor:'Minor', moderate:'Moderate', serious:'Serious', critical:'Critical' };
  const ACTION_LABELS  = {
    verbal_warning:'Verbal Warning', written_warning:'Written Warning', detention:'Detention',
    parent_called:'Parent Called', suspension_in:'In-School Suspension', suspension_out:'Out-of-School Suspension',
    expulsion:'Expulsion', community_service:'Community Service', counselling:'Counselling Referred', no_action:'No Action'
  };
  const INCIDENT_LABELS = {
    misconduct:'Misconduct',fighting:'Fighting/Violence',bullying:'Bullying',truancy:'Truancy',
    vandalism:'Vandalism',substance_abuse:'Substance Abuse',academic_dishonesty:'Academic Dishonesty',
    uniform_violation:'Uniform Violation',insubordination:'Insubordination',other:'Other'
  };

  useEffect(() => {
    apiCall('/parent/academic-years').then(r => {
      setAY(r.data);
      const cur = r.data.find(y => y.is_current);
      if (cur) setYear(cur.id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedYear) return;
    setLoading(true);
    apiCall(`/parent/child/disciplinary?student_id=${student.id}&academic_year_id=${selectedYear}`)
      .then(r => setIncidents(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [student.id, selectedYear]);

  const fmtD = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric',month:'short',year:'numeric'}) : '—';

  return (
    <Box>
      <Box sx={{ display:'flex', gap:2, mb:2, alignItems:'center', flexWrap:'wrap' }}>
        <Typography variant="h6" fontWeight={700} sx={{ flexGrow:1, display:'flex', alignItems:'center', gap:1 }}>
          <Gavel fontSize="small" /> Disciplinary Record
        </Typography>
        <TextField select size="small" label="Term" value={selectedYear} onChange={e => setYear(e.target.value)} sx={{ minWidth:180 }}>
          {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
        </TextField>
      </Box>

      {incidents.length === 0 && !loading ? (
        <Paper variant="outlined" sx={{ p:4, textAlign:'center', borderRadius:2, bgcolor:'#f5fff5' }}>
          <CheckCircle sx={{ fontSize:48, color:'success.main', mb:1 }} />
          <Typography color="success.main" fontWeight={600}>No disciplinary incidents recorded</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt:0.5 }}>
            {student.first_name} has a clean disciplinary record for this term.
          </Typography>
        </Paper>
      ) : loading ? <CircularProgress /> : (
        <>
          {incidents.some(i => !i.resolved && ["serious","critical"].includes(i.severity)) && (
            <Alert severity="error" sx={{ mb:2, fontSize:12 }}>
              There is an unresolved serious/critical incident. Please contact the school urgently.
            </Alert>
          )}
          {incidents.some(i => i.follow_up_required && !i.resolved) && (
            <Alert severity="warning" sx={{ mb:2, fontSize:12 }}>
              Follow-up action is required. Please contact the school.
            </Alert>
          )}
          {incidents.map(inc => (
            <Card key={inc.id} variant="outlined" sx={{ mb:1.5, borderRadius:2,
              borderLeft:`4px solid ${inc.severity==="critical"?"#b71c1c":inc.severity==="serious"?"#c62828":inc.severity==="moderate"?"#e65100":"#aaa"}` }}>
              <CardContent sx={{ pb:"12px !important" }}>
                <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                  <Box>
                    <Typography fontSize={13} fontWeight={700}>{INCIDENT_LABELS[inc.incident_type] || inc.incident_type}{inc.other_description ? ` — ${inc.other_description}` : ""}</Typography>
                    <Typography fontSize={11} color="text.secondary">{fmtD(inc.incident_date)}</Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <Chip label={SEVERITY_LABEL[inc.severity]} size="small" color={SEVERITY_COLOR[inc.severity]} sx={{ fontSize:10, fontWeight:700 }} />
                    <Chip label={inc.resolved ? "Resolved" : "Open"} size="small" color={inc.resolved ? "success" : "warning"} sx={{ fontSize:10 }} />
                  </Stack>
                </Box>
                <Box sx={{ mt:1 }}>
                  <Typography fontSize={11} color="text.secondary" sx={{ mb:0.5 }}>Actions taken:</Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {(inc.action_taken||"").split(",").filter(Boolean).map(a => (
                      <Chip key={a} label={ACTION_LABELS[a]||a} size="small" variant="outlined" sx={{ fontSize:10, mb:0.3 }} />
                    ))}
                  </Stack>
                </Box>
                {inc.suspension_days > 0 && (
                  <Alert severity="warning" sx={{ mt:1, py:0.5, fontSize:11 }}>
                    Suspended {inc.suspension_days} day(s): {fmtD(inc.suspension_start)} to {fmtD(inc.suspension_end)}
                  </Alert>
                )}
                {inc.parent_notified ? (
                  <Typography fontSize={10} color="success.main" sx={{ mt:0.5 }}>✓ School has notified parent/guardian</Typography>
                ) : null}
                {inc.notes ? (
                  <Typography fontSize={11} color="text.secondary" sx={{ mt:0.5, fontStyle:"italic" }}>{inc.notes}</Typography>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </Box>
  );
}


// ── Child Library View ─────────────────────────────────────────────────────────
function ChildLibrary({ student }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiCall(`/parent/child/library?student_id=${student.id}`)
      .then(r => setData(r))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [student.id]);

  const fmtD = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—';
  const fmt  = n => `E${parseFloat(n||0).toFixed(2)}`;

  const daysUntilDue = due => {
    if (!due) return null;
    return Math.ceil((new Date(due) - new Date()) / 86400000);
  };

  if (loading) return <CircularProgress />;
  if (!data) return null;

  const { loans, reservations, summary } = data;
  const activeLoans  = loans?.filter(l => l.status === 'active') || [];
  const overdueLoans = loans?.filter(l => l.status === 'overdue') || [];
  const lostLoans    = loans?.filter(l => l.status === 'lost') || [];
  const returned     = loans?.filter(l => l.status === 'returned') || [];

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2, display:'flex', alignItems:'center', gap:1 }}>
        <MenuBook fontSize="small" /> Library Account
      </Typography>

      {/* Summary cards */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <Paper variant="outlined" sx={{ p:1.5, textAlign:'center', borderRadius:2 }}>
            <Typography variant="h5" fontWeight={800} color="primary">{summary?.active_count || 0}</Typography>
            <Typography variant="caption" color="text.secondary">Books On Loan</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper variant="outlined" sx={{ p:1.5, textAlign:'center', borderRadius:2, bgcolor: summary?.overdue_count > 0 ? '#fff3f3' : 'white' }}>
            <Typography variant="h5" fontWeight={800} color={summary?.overdue_count > 0 ? 'error.main' : 'text.primary'}>{summary?.overdue_count || 0}</Typography>
            <Typography variant="caption" color="text.secondary">Overdue</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper variant="outlined" sx={{ p:1.5, textAlign:'center', borderRadius:2 }}>
            <Typography variant="h5" fontWeight={800} color="text.secondary">{summary?.returned_count || 0}</Typography>
            <Typography variant="caption" color="text.secondary">Returned</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper variant="outlined" sx={{ p:1.5, textAlign:'center', borderRadius:2, bgcolor: summary?.total_fines > 0 ? '#fff3f3' : 'white' }}>
            <Typography variant="h5" fontWeight={800} color={summary?.total_fines > 0 ? 'error.main' : 'text.primary'}>{fmt(summary?.total_fines)}</Typography>
            <Typography variant="caption" color="text.secondary">Outstanding Fines</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Overdue alert */}
      {overdueLoans.length > 0 && (
        <Alert severity="error" sx={{ mb:2, fontSize:12 }}>
          <strong>⚠ {overdueLoans.length} overdue book(s)!</strong> Please return immediately to avoid increasing fines. Contact the school library.
        </Alert>
      )}

      {/* Lost book alert */}
      {lostLoans.length > 0 && (
        <Alert severity="error" sx={{ mb:2, fontSize:12 }}>
          <strong>📕 {lostLoans.length} book(s) reported lost.</strong> A replacement fee must be paid. Please contact the school.
        </Alert>
      )}

      {/* Active loans */}
      {activeLoans.length > 0 && (
        <Box sx={{ mb:2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb:1 }}>📗 Currently Borrowed</Typography>
          {activeLoans.map(l => {
            const days = daysUntilDue(l.due_date);
            const urgent = days !== null && days <= 3;
            return (
              <Card key={l.id} variant="outlined" sx={{ mb:1, borderRadius:2, borderLeft:`4px solid ${urgent?'#e65100':'#2e7d32'}` }}>
                <CardContent sx={{ pb:'8px !important', pt:1.5 }}>
                  <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                    <Box>
                      <Typography fontSize={13} fontWeight={700}>{l.title}</Typography>
                      {l.author && <Typography fontSize={11} color="text.secondary">by {l.author}</Typography>}
                      <Typography fontSize={10} color="text.secondary" fontFamily="monospace">{l.accession_number}</Typography>
                    </Box>
                    <Chip label={l.resource_type} size="small" sx={{ fontSize:9, textTransform:'capitalize' }} />
                  </Box>
                  <Box sx={{ display:'flex', gap:2, mt:1, flexWrap:'wrap' }}>
                    <Typography fontSize={11} color="text.secondary">📅 Borrowed: {fmtD(l.issued_date)}</Typography>
                    <Typography fontSize={11} sx={{ color: urgent ? 'error.main' : 'success.main', fontWeight: urgent ? 700 : 400 }}>
                      {urgent ? `⏰ Due in ${days} day(s): ` : '✓ Due: '}{fmtD(l.due_date)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Overdue loans */}
      {overdueLoans.length > 0 && (
        <Box sx={{ mb:2 }}>
          <Typography variant="subtitle2" fontWeight={700} color="error" sx={{ mb:1 }}>⚠ Overdue Books</Typography>
          {overdueLoans.map(l => (
            <Card key={l.id} variant="outlined" sx={{ mb:1, borderRadius:2, borderLeft:'4px solid #c62828', bgcolor:'#fff3f3' }}>
              <CardContent sx={{ pb:'8px !important', pt:1.5 }}>
                <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                  <Box>
                    <Typography fontSize={13} fontWeight={700}>{l.title}</Typography>
                    {l.author && <Typography fontSize={11} color="text.secondary">by {l.author}</Typography>}
                  </Box>
                  {l.fine_amount > 0 && <Chip label={`Fine: ${fmt(l.fine_amount)}`} size="small" color="error" sx={{ fontWeight:700 }} />}
                </Box>
                <Box sx={{ display:'flex', gap:2, mt:1, flexWrap:'wrap' }}>
                  <Typography fontSize={11} color="text.secondary">Borrowed: {fmtD(l.issued_date)}</Typography>
                  <Typography fontSize={11} color="error.main" fontWeight={700}>Was due: {fmtD(l.due_date)}</Typography>
                </Box>
                <Typography fontSize={11} color="error.main" sx={{ mt:0.5 }}>
                  Please return this book to the library immediately.
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Lost books */}
      {lostLoans.length > 0 && (
        <Box sx={{ mb:2 }}>
          <Typography variant="subtitle2" fontWeight={700} color="error" sx={{ mb:1 }}>📕 Lost Books</Typography>
          {lostLoans.map(l => (
            <Card key={l.id} variant="outlined" sx={{ mb:1, borderRadius:2, borderLeft:'4px solid #b71c1c', bgcolor:'#fce4e4' }}>
              <CardContent sx={{ pb:'8px !important', pt:1.5 }}>
                <Box sx={{ display:'flex', justifyContent:'space-between' }}>
                  <Box>
                    <Typography fontSize={13} fontWeight={700}>{l.title}</Typography>
                    {l.author && <Typography fontSize={11} color="text.secondary">by {l.author}</Typography>}
                    <Typography fontSize={11} color="text.secondary">Borrowed: {fmtD(l.issued_date)}</Typography>
                  </Box>
                  <Chip label="LOST" size="small" color="error" sx={{ fontWeight:800 }} />
                </Box>
                <Alert severity="error" sx={{ mt:1, py:0.5, fontSize:11 }}>
                  A replacement fee applies. Please contact the school finance office.
                </Alert>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Pending reservations */}
      {reservations?.length > 0 && (
        <Box sx={{ mb:2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb:1 }}>🔖 Pending Reservations</Typography>
          {reservations.map(r => (
            <Card key={r.id} variant="outlined" sx={{ mb:1, borderRadius:2, borderLeft:'4px solid #1565c0' }}>
              <CardContent sx={{ pb:'8px !important', pt:1.5 }}>
                <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <Box>
                    <Typography fontSize={13} fontWeight={700}>{r.title}</Typography>
                    {r.author && <Typography fontSize={11} color="text.secondary">by {r.author}</Typography>}
                    <Typography fontSize={11} color="text.secondary">Reserved: {fmtD(r.reserved_date)} · Expires: {fmtD(r.expiry_date)}</Typography>
                  </Box>
                  <Chip label={r.available_copies > 0 ? 'Available!' : 'Waiting'} size="small"
                    color={r.available_copies > 0 ? 'success' : 'warning'} sx={{ fontWeight:700 }} />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Return history */}
      {returned.length > 0 && (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb:1 }}>📚 Return History</Typography>
          <Card variant="outlined" sx={{ borderRadius:2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight:700, fontSize:11 }}>Title</TableCell>
                    <TableCell sx={{ fontWeight:700, fontSize:11 }}>Borrowed</TableCell>
                    <TableCell sx={{ fontWeight:700, fontSize:11 }}>Returned</TableCell>
                    <TableCell sx={{ fontWeight:700, fontSize:11 }}>Fine</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {returned.map(l => (
                    <TableRow key={l.id} hover>
                      <TableCell>
                        <Typography fontSize={12} fontWeight={600}>{l.title}</Typography>
                        {l.author && <Typography fontSize={10} color="text.secondary">{l.author}</Typography>}
                      </TableCell>
                      <TableCell sx={{ fontSize:11 }}>{fmtD(l.issued_date)}</TableCell>
                      <TableCell sx={{ fontSize:11 }}>{fmtD(l.returned_date)}</TableCell>
                      <TableCell sx={{ fontSize:11 }}>{l.fine_amount > 0 ? <Chip label={fmt(l.fine_amount)} size="small" color={l.fine_paid ? 'default' : 'error'} sx={{ fontSize:10 }} /> : <CheckCircle sx={{ fontSize:14, color:'success.main' }} />}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Box>
      )}

      {!loans?.length && !reservations?.length && (
        <Paper variant="outlined" sx={{ p:4, textAlign:'center', borderRadius:2 }}>
          <MenuBook sx={{ fontSize:48, color:'text.disabled', mb:1 }} />
          <Typography color="text.secondary">No library activity for {student.first_name}</Typography>
        </Paper>
      )}
    </Box>
  );
}


// ── Child Homework View ────────────────────────────────────────────────────────
function ChildHomework({ student }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [academicYears, setAY] = useState([]);
  const [selectedYear, setYear] = useState('');

  useEffect(() => {
    apiCall('/parent/academic-years').then(r => {
      setAY(r.data);
      const cur = r.data.find(y => y.is_current);
      if (cur) setYear(cur.id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedYear) return;
    setLoading(true);
    apiCall(`/parent/child/homework?student_id=${student.id}&academic_year_id=${selectedYear}`)
      .then(r => { setData(r); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [student.id, selectedYear]);

  const fmtD = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—';
  const daysUntil = d => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null;

  const STATUS_COLOR = { pending:'warning', submitted:'success', late:'warning', missing:'error', excused:'default' };
  const STATUS_LABEL = { pending:'Pending', submitted:'Submitted', late:'Late', missing:'Missing', excused:'Excused' };
  const PRIORITY_COLOR = { low:'default', normal:'primary', high:'error' };

  const pending   = data?.data?.filter(h => h.submission_status === 'pending') || [];
  const missing   = data?.data?.filter(h => h.submission_status === 'missing') || [];
  const submitted = data?.data?.filter(h => ['submitted','late'].includes(h.submission_status)) || [];

  return (
    <Box>
      <Box sx={{ display:'flex', gap:2, mb:2, alignItems:'center', flexWrap:'wrap' }}>
        <Typography variant="h6" fontWeight={700} sx={{ flexGrow:1, display:'flex', alignItems:'center', gap:1 }}>
          <HWIcon fontSize="small" /> Homework
        </Typography>
        <TextField select size="small" label="Term" value={selectedYear} onChange={e => setYear(e.target.value)} sx={{ minWidth:180 }}>
          {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
        </TextField>
      </Box>

      {/* Summary */}
      {data && (
        <Grid container spacing={1.5} sx={{ mb:2 }}>
          <Grid item xs={4}>
            <Paper variant="outlined" sx={{ p:1.5, textAlign:'center', borderRadius:2 }}>
              <Typography variant="h5" fontWeight={800} color="warning.main">{data.summary?.pending || 0}</Typography>
              <Typography variant="caption" color="text.secondary">Pending</Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper variant="outlined" sx={{ p:1.5, textAlign:'center', borderRadius:2, bgcolor: missing.length > 0 ? '#fff3f3' : 'white' }}>
              <Typography variant="h5" fontWeight={800} color={missing.length > 0 ? 'error.main' : 'text.primary'}>{data.summary?.missing || 0}</Typography>
              <Typography variant="caption" color="text.secondary">Missing</Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper variant="outlined" sx={{ p:1.5, textAlign:'center', borderRadius:2 }}>
              <Typography variant="h5" fontWeight={800} color="success.main">{data.summary?.submitted || 0}</Typography>
              <Typography variant="caption" color="text.secondary">Submitted</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {missing.length > 0 && (
        <Alert severity="error" sx={{ mb:2, fontSize:12 }}>
          <strong>{missing.length} missing assignment(s).</strong> Please ensure {student.first_name} completes and submits these urgently.
        </Alert>
      )}

      {loading ? <CircularProgress /> : !data?.data?.length ? (
        <Paper variant="outlined" sx={{ p:4, textAlign:'center', borderRadius:2 }}>
          <HWIcon sx={{ fontSize:48, color:'text.disabled', mb:1 }} />
          <Typography color="text.secondary">No homework assigned for this term</Typography>
        </Paper>
      ) : (
        <Box>
          {/* Pending / Missing first */}
          {[...missing, ...pending].length > 0 && (
            <Box sx={{ mb:2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb:1 }}>📋 Needs Attention</Typography>
              {[...missing, ...pending].map(h => {
                const days = daysUntil(h.due_date);
                const isOverdue = h.submission_status === 'missing' || (days !== null && days < 0);
                return (
                  <Card key={h.id} variant="outlined" sx={{ mb:1, borderRadius:2,
                    borderLeft:`4px solid ${h.submission_status==='missing'?'#c62828':'#e65100'}`,
                    bgcolor: h.submission_status==='missing' ? '#fff3f3' : 'white' }}>
                    <CardContent sx={{ pb:'8px !important', pt:1.5 }}>
                      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                        <Box>
                          <Typography fontSize={13} fontWeight={700}>{h.title}</Typography>
                          <Typography fontSize={11} color="text.secondary">{h.subject_name} · {h.teacher_name}</Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          <Chip label={STATUS_LABEL[h.submission_status]} size="small"
                            color={STATUS_COLOR[h.submission_status]} sx={{ fontSize:10, fontWeight:700 }} />
                          {h.priority === 'high' && <Chip label="High" size="small" color="error" sx={{ fontSize:10 }} />}
                        </Stack>
                      </Box>
                      <Typography fontSize={11} sx={{ color: isOverdue?'error.main':'warning.main', mt:0.5, fontWeight:600 }}>
                        {h.submission_status === 'missing' ? `Was due: ${fmtD(h.due_date)}` :
                          days === 0 ? 'Due today!' : days === 1 ? 'Due tomorrow' :
                          days !== null && days > 0 ? `Due in ${days} day(s) — ${fmtD(h.due_date)}` : `Due: ${fmtD(h.due_date)}`}
                      </Typography>
                      {h.description && (
                        <Typography fontSize={11} color="text.secondary" sx={{ mt:0.5 }}>{h.description}</Typography>
                      )}
                      {h.estimated_duration && (
                        <Typography fontSize={10} color="text.secondary">Est. time: {h.estimated_duration} min</Typography>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}

          {/* Submitted */}
          {submitted.length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb:1 }}>✅ Submitted</Typography>
              {submitted.map(h => (
                <Card key={h.id} variant="outlined" sx={{ mb:1, borderRadius:2, borderLeft:'4px solid #2e7d32', bgcolor:'#f5fff5' }}>
                  <CardContent sx={{ pb:'8px !important', pt:1.5 }}>
                    <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <Box>
                        <Typography fontSize={13} fontWeight={700}>{h.title}</Typography>
                        <Typography fontSize={11} color="text.secondary">{h.subject_name}</Typography>
                        <Typography fontSize={10} color="text.secondary">Due: {fmtD(h.due_date)}</Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip label={STATUS_LABEL[h.submission_status]} size="small"
                          color={STATUS_COLOR[h.submission_status]} sx={{ fontSize:10, fontWeight:700 }} />
                        {h.mark !== null && h.mark !== undefined && (
                          <Chip label={`${h.mark}/100`} size="small"
                            color={h.mark >= 70?'success':h.mark>=50?'warning':'error'}
                            sx={{ fontSize:10, fontWeight:700 }} />
                        )}
                      </Stack>
                    </Box>
                    {h.feedback && (
                      <Alert severity="info" sx={{ mt:1, py:0.5, fontSize:11 }}>
                        <strong>Teacher feedback:</strong> {h.feedback}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}


// ── Dashboard (after login) ───────────────────────────────────────────────────
function Dashboard({ parent, children: initialChildren, onLogout }) {
  const { enqueueSnackbar } = useSnackbar();
  const [children, setChildren]       = useState(initialChildren);
  const [activeChild, setActiveChild] = useState(initialChildren[0] || null);
  const [tab, setTab]                 = useState(0);
  const [addDialog, setAddDialog]     = useState(false);

  const refreshChildren = () => {
    apiCall('/parent/children').then(r => {
      setChildren(r.data);
      if (!activeChild && r.data.length) setActiveChild(r.data[0]);
    }).catch(() => {});
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f6fa' }}>
      {/* Top bar */}
      <AppBar position="static" sx={{ bgcolor: '#0f2b4a' }} elevation={0}>
        <Toolbar sx={{ gap: 1 }}>
          <School sx={{ mr: 1 }} />
          <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1 }}>Parent Portal</Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>{parent.full_name}</Typography>
          <IconButton color="inherit" onClick={onLogout} size="small"><Logout /></IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ maxWidth: 860, mx: 'auto', p: { xs: 1.5, sm: 3 } }}>
        {/* Child selector */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3, alignItems: 'center' }}>
          {children.map(child => (
            <Chip key={child.id}
              avatar={<Avatar sx={{ bgcolor: activeChild?.id === child.id ? 'white' : '#0f2b4a' }}>
                {child.first_name?.[0]}
              </Avatar>}
              label={`${child.first_name} ${child.last_name}`}
              onClick={() => { setActiveChild(child); setTab(0); }}
              color={activeChild?.id === child.id ? 'primary' : 'default'}
              variant={activeChild?.id === child.id ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, fontSize: 13 }}
            />
          ))}
          <Chip icon={<Add />} label="Add Child" variant="outlined" onClick={() => setAddDialog(true)}
            sx={{ borderStyle: 'dashed', cursor: 'pointer' }} />
        </Box>

        {activeChild && (
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent sx={{ pb: '12px !important' }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ bgcolor: '#0f2b4a', width: 48, height: 48, fontSize: 18 }}>
                  {activeChild.first_name?.[0]}{activeChild.last_name?.[0]}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography fontWeight={700} fontSize={16}>
                    {activeChild.first_name} {activeChild.last_name}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip label={activeChild.admission_number} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                    <Chip label={activeChild.class_name || 'Class N/A'} size="small" color="primary" sx={{ fontSize: 11 }} />
                    <Chip icon={<Verified sx={{ fontSize: 12 }} />} label="Verified" size="small" color="success" sx={{ fontSize: 11 }} />
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, bgcolor: 'white', borderRadius: 1 }}>
          <Tab icon={<CalendarMonth fontSize="small" />} label="Attendance" iconPosition="start" sx={{ fontSize: 12 }} />
          <Tab icon={<BarChart fontSize="small" />} label="Assessment Scores" iconPosition="start" sx={{ fontSize: 12 }} />
          <Tab icon={<AccountBalance fontSize="small" />} label="Fees" iconPosition="start" sx={{ fontSize: 12 }} />
          <Tab icon={<Gavel fontSize="small" />} label="Disciplinary" iconPosition="start" sx={{ fontSize: 12 }} />
          <Tab icon={<MenuBook fontSize="small" />} label="Library" iconPosition="start" sx={{ fontSize: 12 }} />
          <Tab icon={<HWIcon fontSize="small" />} label="Homework" iconPosition="start" sx={{ fontSize: 12 }} />
        </Tabs>

        {activeChild && tab === 0 && <ChildAttendance student={activeChild} />}
        {activeChild && tab === 1 && <ChildScores student={activeChild} />}
        {activeChild && tab === 2 && <ChildFees student={activeChild} />}
        {activeChild && tab === 3 && <ChildDisciplinary student={activeChild} />}
        {activeChild && tab === 4 && <ChildLibrary student={activeChild} />}
        {activeChild && tab === 5 && <ChildHomework student={activeChild} />}

        {!activeChild && (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
            <ChildCare sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No children linked yet. Click "Add Child" to get started.</Typography>
          </Paper>
        )}
      </Box>

      {/* Add child dialog */}
      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Link Another Child</DialogTitle>
        <DialogContent dividers>
          <AddChildForm parentPhone={parent.phone} onSuccess={() => { setAddDialog(false); refreshChildren(); enqueueSnackbar('Child linked successfully', { variant: 'success' }); }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Add child form (from within dashboard) ────────────────────────────────────
function AddChildForm({ parentPhone, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [classes, setClasses] = useState([]);
  const [years, setYears]     = useState([]);
  const [form, setForm]       = useState({
    admission_number: '', class_id: '', gender: '', parent_phone: parentPhone,
    date_of_birth: '', full_name: 'existing', password: '', academic_year_id: ''
  });

  useEffect(() => {
    apiCall('/parent/classes').then(r => setClasses(r.data)).catch(() => {});
    apiCall('/parent/academic-years').then(r => {
      setYears(r.data);
      const cur = r.data.find(y => y.is_current);
      if (cur) setForm(f => ({ ...f, academic_year_id: cur.id }));
    }).catch(() => {});
  }, []);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setError(''); setLoading(true);
    try {
      await apiCall('/parent/register', { method: 'POST', body: JSON.stringify(form) });
      onSuccess();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <Grid container spacing={2} sx={{ pt: 1 }}>
      {error && <Grid item xs={12}><Alert severity="error">{error}</Alert></Grid>}
      <Grid item xs={12}><Alert severity="info" sx={{ fontSize: 12 }}>Enter this child's school details. Your existing account password is required to link them.</Alert></Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Admission Number *" value={form.admission_number} onChange={set('admission_number')} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField select fullWidth size="small" label="Class *" value={form.class_id} onChange={set('class_id')}>
          <MenuItem value="">Select…</MenuItem>
          {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}{c.stream ? ` (${c.stream})` : ''}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField select fullWidth size="small" label="Gender *" value={form.gender} onChange={set('gender')}>
          <MenuItem value="">Select…</MenuItem>
          <MenuItem value="male">Male</MenuItem>
          <MenuItem value="female">Female</MenuItem>
          <MenuItem value="other">Other</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" type="date" label="Date of Birth *" value={form.date_of_birth} onChange={set('date_of_birth')} InputLabelProps={{ shrink: true }} />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth size="small" type="password" label="Your Account Password *" value={form.password} onChange={set('password')} helperText="Enter your existing portal password to confirm" />
      </Grid>
      <Grid item xs={12}>
        <Button fullWidth variant="contained" onClick={submit} disabled={loading} sx={{ bgcolor: '#0f2b4a' }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Link Child'}
        </Button>
      </Grid>
    </Grid>
  );
}

// ── Root Parent Portal ────────────────────────────────────────────────────────
export default function ParentPortal() {
  const { slug } = useParams(); // present when accessed via /school/:slug/parent
  const schoolSlug = slug || localStorage.getItem('parent_slug') || '';

  const [view, setView]         = useState('login'); // login | register | dashboard
  const [parent, setParent]     = useState(null);
  const [children, setChildren] = useState([]);

  // Check for existing session
  useEffect(() => {
    const token = localStorage.getItem('parent_token');
    if (!token) return;
    apiCall('/parent/children')
      .then(r => {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setParent({ id: payload.parentId, full_name: 'Parent' });
        setChildren(r.data);
        setView('dashboard');
      })
      .catch(() => localStorage.removeItem('parent_token'));
  }, []);

  const handleLogin = (p, c) => { setParent(p); setChildren(c || []); setView('dashboard'); };
  const handleLogout = () => {
    localStorage.removeItem('parent_token');
    localStorage.removeItem('parent_slug');
    setParent(null); setChildren([]); setView('login');
    if (schoolSlug) window.location.href = `/school/${schoolSlug}/parent`;
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f6fa', pb: 4 }}>
      {view === 'login'     && <LoginPage onLogin={handleLogin} onRegister={() => setView('register')} schoolSlug={schoolSlug} />}
      {view === 'register'  && (
        <Box>
          <Box sx={{ p: 2 }}>
            <Button startIcon={<ArrowBack />} onClick={() => setView('login')}>Back to Login</Button>
          </Box>
          <RegisterPage onSuccess={() => setView('login')} />
        </Box>
      )}
      {view === 'dashboard' && parent && (
        <Dashboard parent={parent} children={children} onLogout={handleLogout} />
      )}
    </Box>
  );
}
